import React, { useState, useEffect, useCallback } from "react";
import { Tabs, Modal, Button, Spin, message } from "antd";
import {
  DownloadOutlined,
  FolderOutlined,
  TeamOutlined,
  CheckSquareOutlined,
  UserOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  InboxOutlined,
  SendOutlined,
} from "@ant-design/icons";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import { useSelector } from "react-redux";
import { propertyService } from "@iso/services";
import { attachment } from "@iso/services/attachment.service";
import LibraryTab from "@iso/components/FileBrowser/LibraryTab";
import UploadTab from "@iso/components/FileBrowser/UploadTab";
import { getFileIcon, getFileName } from "@iso/components/FileBrowser/fileUtils";
import FileManagerWrapper, { FileManagerModalGlobalStyles } from "./FileManager.styles";
import useCanManageFiles from "./useCanManageFiles";
import AssignUserModal from "./AssignUserModal";
import SetExpiryModal from "./SetExpiryModal";
import ShareFileModal from "./ShareFileModal";
import SharedFilesTab from "./SharedFilesTab";

const { TabPane } = Tabs;

const FileManager = () => {
  const activePropertyId = useSelector(
    (state) => state.property.activePropertyId
  );
  const { canManageProperty, canManageCorporate } = useCanManageFiles();
  const [corporateGroupId, setCorporateGroupId] = useState(null);
  const [propertyLoaded, setPropertyLoaded] = useState(false);
  const [propertyRefreshKey, setPropertyRefreshKey] = useState(0);
  const [corporateRefreshKey, setCorporateRefreshKey] = useState(0);
  const [propertyFolderId, setPropertyFolderId] = useState(null);
  const [corporateFolderId, setCorporateFolderId] = useState(null);
  const [activeScope, setActiveScope] = useState("property");

  // Bulk-select state, scoped per tab.
  const [bulkMode, setBulkMode] = useState(false);
  const [propertyBulkIds, setPropertyBulkIds] = useState([]);
  const [corporateBulkIds, setCorporateBulkIds] = useState([]);

  // Preview modal state
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // Assign / expiry modal state. `targetFiles` is the file(s) the modal acts on
  // — either a single file (icon click) or the current bulk selection.
  const [assignUserVisible, setAssignUserVisible] = useState(false);
  const [setExpiryVisible, setSetExpiryVisible] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [targetFiles, setTargetFiles] = useState([]);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    if (activePropertyId) {
      setPropertyLoaded(false);
      propertyService.viewProperty(activePropertyId).then((res) => {
        if (res.code === "0000") {
          setCorporateGroupId(res.property.corporate_group_id || null);
        }
        setPropertyLoaded(true);
      });
    } else {
      setCorporateGroupId(null);
      setPropertyLoaded(false);
    }
  }, [activePropertyId]);

  // Reset bulk selections when scope/tab changes — files in one scope are not
  // valid ids in the other.
  useEffect(() => {
    setPropertyBulkIds([]);
    setCorporateBulkIds([]);
  }, [activeScope, activePropertyId, corporateGroupId, bulkMode]);

  const handlePropertyUploadSuccess = useCallback(() => {
    setPropertyRefreshKey((prev) => prev + 1);
  }, []);

  const handleCorporateUploadSuccess = useCallback(() => {
    setCorporateRefreshKey((prev) => prev + 1);
  }, []);

  const refreshActiveScope = useCallback(() => {
    if (activeScope === "corporate") {
      setCorporateRefreshKey((prev) => prev + 1);
    } else {
      setPropertyRefreshKey((prev) => prev + 1);
    }
  }, [activeScope]);

  const scopePayload = useCallback(() => {
    if (activeScope === "corporate" && corporateGroupId) {
      return { corporate_group_id: corporateGroupId };
    }
    return { property_id: activePropertyId };
  }, [activeScope, corporateGroupId, activePropertyId]);

  const bulkSelectedIds =
    activeScope === "corporate" ? corporateBulkIds : propertyBulkIds;
  const setBulkSelectedIds =
    activeScope === "corporate" ? setCorporateBulkIds : setPropertyBulkIds;
  // Manager affordances are scope-specific: in corporate library a user may
  // be admin on a sibling property in the group even if they are not admin
  // on the active property, and vice versa.
  const canManage =
    activeScope === "corporate" ? canManageCorporate : canManageProperty;

  const handleFileAction = useCallback(
    async (action, file) => {
      switch (action) {
        case "preview":
          setPreviewFile(file);
          setPreviewVisible(true);
          break;
        case "download":
          if (file.full_path) {
            try {
              const response = await fetch(file.full_path);
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = getFileName(file);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            } catch (err) {
              window.open(file.full_path, "_blank");
            }
          }
          break;
        case "delete":
          Modal.confirm({
            title: "Delete File",
            content: `Are you sure you want to delete "${getFileName(file)}"? This action cannot be undone.`,
            okText: "Delete",
            okType: "danger",
            onOk: async () => {
              try {
                const res = await attachment.destroy(file.id);
                if (res && res.code === "0000") {
                  message.success("File deleted");
                  refreshActiveScope();
                } else {
                  message.error(res?.message || "Failed to delete file");
                }
              } catch (error) {
                message.error("Failed to delete file");
              }
            },
          });
          break;
        case "assignUser":
          setTargetFiles([file]);
          setAssignUserVisible(true);
          break;
        case "setExpiry":
          setTargetFiles([file]);
          setSetExpiryVisible(true);
          break;
        case "share":
          setShareTarget(file);
          setShareModalVisible(true);
          break;
        default:
          break;
      }
    },
    [refreshActiveScope]
  );

  const closeAssignUser = useCallback(() => {
    setAssignUserVisible(false);
    setTargetFiles([]);
  }, []);

  const closeSetExpiry = useCallback(() => {
    setSetExpiryVisible(false);
    setTargetFiles([]);
  }, []);

  const submitAssignUser = useCallback(
    async (userId) => {
      setModalSubmitting(true);
      try {
        const ids = targetFiles.map((f) => f.id);
        if (ids.length === 1) {
          const res = await attachment.updateAttachment(ids[0], {
            user_id: userId,
          });
          if (res && res.code === "0000") {
            message.success("User assigned");
          } else {
            message.error(res?.message || "Failed to assign user");
          }
        } else {
          const res = await attachment.bulkUpdate({
            ids,
            ...scopePayload(),
            user_id: userId,
          });
          if (res && res.code === "0000") {
            message.success(`User assigned to ${ids.length} files`);
            setBulkSelectedIds([]);
          } else {
            message.error(res?.message || "Failed to assign user");
          }
        }
        closeAssignUser();
        refreshActiveScope();
      } catch (err) {
        message.error("Failed to assign user");
      } finally {
        setModalSubmitting(false);
      }
    },
    [targetFiles, scopePayload, setBulkSelectedIds, refreshActiveScope]
  );

  const submitSetExpiry = useCallback(
    async (expiryDate) => {
      setModalSubmitting(true);
      try {
        const ids = targetFiles.map((f) => f.id);
        if (ids.length === 1) {
          const res = await attachment.updateAttachment(ids[0], {
            expiry_date: expiryDate,
          });
          if (res && res.code === "0000") {
            message.success("Expiry updated");
          } else {
            message.error(res?.message || "Failed to update expiry");
          }
        } else {
          const res = await attachment.bulkUpdate({
            ids,
            ...scopePayload(),
            expiry_date: expiryDate,
          });
          if (res && res.code === "0000") {
            message.success(`Expiry updated on ${ids.length} files`);
            setBulkSelectedIds([]);
          } else {
            message.error(res?.message || "Failed to update expiry");
          }
        }
        closeSetExpiry();
        refreshActiveScope();
      } catch (err) {
        message.error("Failed to update expiry");
      } finally {
        setModalSubmitting(false);
      }
    },
    [targetFiles, scopePayload, setBulkSelectedIds, refreshActiveScope]
  );

  const handleBulkAssignUser = () => {
    setTargetFiles(bulkSelectedIds.map((id) => ({ id })));
    setAssignUserVisible(true);
  };

  const handleBulkSetExpiry = () => {
    setTargetFiles(bulkSelectedIds.map((id) => ({ id })));
    setSetExpiryVisible(true);
  };

  const handleBulkDownload = async () => {
    if (!bulkSelectedIds.length) return;
    setBulkBusy(true);
    try {
      const data = await attachment.bulkDownload({
        ids: bulkSelectedIds,
        ...scopePayload(),
      });
      if (!data) {
        message.error("Failed to download files");
        return;
      }
      const blob = new Blob([data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `files-${new Date()
        .toISOString()
        .replace(/[-:T]/g, "")
        .slice(0, 14)}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success("Download started");
    } catch (err) {
      message.error("Failed to download files");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDelete = () => {
    if (!bulkSelectedIds.length) return;
    Modal.confirm({
      title: `Delete ${bulkSelectedIds.length} files?`,
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        setBulkBusy(true);
        try {
          const res = await attachment.bulkDelete({
            ids: bulkSelectedIds,
            ...scopePayload(),
          });
          if (res && res.code === "0000") {
            message.success(`Deleted ${res.data?.deleted_count ?? bulkSelectedIds.length} files`);
            setBulkSelectedIds([]);
            refreshActiveScope();
          } else {
            message.error(res?.message || "Failed to delete files");
          }
        } catch (err) {
          message.error("Failed to delete files");
        } finally {
          setBulkBusy(false);
        }
      },
    });
  };

  const renderPreviewContent = () => {
    if (!previewFile) return null;
    const name = getFileName(previewFile);

    if (previewFile.type === "image" && previewFile.full_path) {
      return (
        <div style={{ textAlign: "center" }}>
          <img
            src={previewFile.full_path}
            alt={name}
            style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: 4, cursor: "pointer" }}
            onClick={() => window.open(previewFile.full_path, "_blank", "noopener,noreferrer")}
          />
        </div>
      );
    }

    if (previewFile.type === "video" && previewFile.full_path) {
      return (
        <video controls src={previewFile.full_path} style={{ width: "100%" }} />
      );
    }

    if (previewFile.type === "audio" && previewFile.full_path) {
      return (
        <audio controls src={previewFile.full_path} style={{ width: "100%" }} />
      );
    }

    if (previewFile.type === "pdf" && previewFile.full_path) {
      return (
        <iframe
          src={previewFile.full_path}
          title={name}
          style={{ width: "100%", height: "70vh", border: "none" }}
        />
      );
    }

    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{ fontSize: 64, marginBottom: 16, color: "#999" }}>
          {getFileIcon(previewFile.type)}
        </div>
        <p style={{ fontSize: 16, color: "#333", marginBottom: 4 }}>{name}</p>
        <p style={{ fontSize: 13, color: "#999", textTransform: "uppercase", marginBottom: 20 }}>
          {previewFile.type}
        </p>
        {previewFile.full_path && (
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => handleFileAction("download", previewFile)}
          >
            Download
          </Button>
        )}
      </div>
    );
  };

  if (!activePropertyId) {
    return (
      <LayoutWrapper>
        <FileManagerWrapper>
          <Box id="containerBox">
            <div className="manage-property-header">
              <h2 className="manage-property-title" style={{ margin: 0 }}>
                File Manager
              </h2>
            </div>
            <div style={{ padding: "40px 24px", textAlign: "center", color: "#999" }}>
              Please select a property first.
            </div>
          </Box>
        </FileManagerWrapper>
      </LayoutWrapper>
    );
  }

  // Look up the assigned user / expiry of the single-file target so the modal
  // pickers preselect the current value.
  const singleTargetFile = targetFiles.length === 1 ? targetFiles[0] : null;
  const initialAssignedUserId = singleTargetFile?.user?.id ?? null;
  const initialExpiry = singleTargetFile?.expiry_date ?? null;

  const renderToolbar = () => (
    <div className="file-manager-toolbar">
      <Button
        type={bulkMode ? "primary" : "default"}
        icon={<CheckSquareOutlined />}
        onClick={() => setBulkMode((prev) => !prev)}
      >
        {bulkMode ? "Done" : "Select multiple"}
      </Button>
    </div>
  );

  const renderBulkActionBar = () => {
    if (!bulkMode || bulkSelectedIds.length === 0) return null;
    return (
      <div className="bulk-action-bar">
        <span className="bulk-count">
          {bulkSelectedIds.length} selected
        </span>
        {canManage && (
          <Button
            icon={<UserOutlined />}
            disabled={bulkBusy}
            onClick={handleBulkAssignUser}
          >
            Assign user
          </Button>
        )}
        {canManage && (
          <Button
            icon={<ClockCircleOutlined />}
            disabled={bulkBusy}
            onClick={handleBulkSetExpiry}
          >
            Set expiry
          </Button>
        )}
        <Button
          icon={<DownloadOutlined />}
          loading={bulkBusy}
          onClick={handleBulkDownload}
        >
          Download
        </Button>
        <Button
          danger
          icon={<DeleteOutlined />}
          disabled={bulkBusy}
          onClick={handleBulkDelete}
        >
          Delete
        </Button>
        <Button
          type="text"
          style={{ color: "#fff", marginLeft: "auto" }}
          onClick={() => setBulkSelectedIds([])}
        >
          Clear
        </Button>
      </div>
    );
  };

  return (
    <LayoutWrapper>
      <FileManagerModalGlobalStyles />
      <FileManagerWrapper>
        <Box id="containerBox">
          <div className="manage-property-header">
            <h2 className="manage-property-title" style={{ margin: 0 }}>
              File Manager
            </h2>
          </div>
          <div className="file-manager-content">
            <Tabs
              activeKey={activeScope}
              onChange={(key) => setActiveScope(key)}
              size="large"
              type="card"
            >
              <TabPane
                tab={<span><FolderOutlined /> My Files</span>}
                key="property"
              >
                <Tabs defaultActiveKey="library" size="small">
                  <TabPane tab="Library" key="library">
                    {renderToolbar()}
                    <LibraryTab
                      selectable={false}
                      onFileAction={handleFileAction}
                      refreshKey={propertyRefreshKey}
                      propertyId={activePropertyId}
                      onFolderChange={setPropertyFolderId}
                      bulkMode={bulkMode}
                      bulkSelectedIds={propertyBulkIds}
                      onBulkSelectionChange={setPropertyBulkIds}
                      canManage={canManageProperty}
                    />
                    {activeScope === "property" && renderBulkActionBar()}
                  </TabPane>
                  <TabPane tab="Upload" key="upload">
                    <UploadTab
                      onUploadSuccess={handlePropertyUploadSuccess}
                      currentFolderId={propertyFolderId}
                    />
                  </TabPane>
                </Tabs>
              </TabPane>

              <TabPane
                tab={<span><InboxOutlined /> Shared with me</span>}
                key="shared-received"
              >
                <SharedFilesTab
                  direction="received"
                  onPreview={(file) => {
                    setPreviewFile(file);
                    setPreviewVisible(true);
                  }}
                />
              </TabPane>

              <TabPane
                tab={<span><SendOutlined /> Shared with others</span>}
                key="shared-sent"
              >
                <SharedFilesTab direction="sent" />
              </TabPane>

              {(!propertyLoaded || corporateGroupId) && (
                <TabPane
                  tab={<span><TeamOutlined /> Corporate Library</span>}
                  key="corporate"
                >
                  {corporateGroupId ? (
                    <Tabs defaultActiveKey="library" size="small">
                      <TabPane tab="Library" key="library">
                        {renderToolbar()}
                        <LibraryTab
                          selectable={false}
                          onFileAction={handleFileAction}
                          refreshKey={corporateRefreshKey}
                          corporateGroupId={corporateGroupId}
                          onFolderChange={setCorporateFolderId}
                          bulkMode={bulkMode}
                          bulkSelectedIds={corporateBulkIds}
                          onBulkSelectionChange={setCorporateBulkIds}
                          canManage={canManageCorporate}
                        />
                        {activeScope === "corporate" && renderBulkActionBar()}
                      </TabPane>
                      <TabPane tab="Upload" key="upload">
                        <UploadTab
                          onUploadSuccess={handleCorporateUploadSuccess}
                          currentFolderId={corporateFolderId}
                          corporateGroupId={corporateGroupId}
                        />
                      </TabPane>
                    </Tabs>
                  ) : (
                    <div style={{ textAlign: "center", padding: "60px 0" }}>
                      <Spin />
                    </div>
                  )}
                </TabPane>
              )}
            </Tabs>
          </div>
        </Box>
      </FileManagerWrapper>

      {/* File Preview Modal */}
      <Modal
        visible={previewVisible}
        title={previewFile ? getFileName(previewFile) : "Preview"}
        footer={null}
        onCancel={() => {
          setPreviewVisible(false);
          setPreviewFile(null);
        }}
        width={800}
        destroyOnClose
      >
        {renderPreviewContent()}
      </Modal>

      <AssignUserModal
        visible={assignUserVisible}
        propertyId={activeScope === "corporate" ? null : activePropertyId}
        corporateGroupId={activeScope === "corporate" ? corporateGroupId : null}
        initialUserId={initialAssignedUserId}
        fileCount={targetFiles.length}
        confirmLoading={modalSubmitting}
        onCancel={closeAssignUser}
        onConfirm={submitAssignUser}
      />

      <SetExpiryModal
        visible={setExpiryVisible}
        initialExpiry={initialExpiry}
        fileCount={targetFiles.length}
        confirmLoading={modalSubmitting}
        onCancel={closeSetExpiry}
        onConfirm={submitSetExpiry}
      />

      <ShareFileModal
        visible={shareModalVisible}
        existingFile={shareTarget}
        propertyId={activeScope === "corporate" ? null : activePropertyId}
        corporateGroupId={activeScope === "corporate" ? corporateGroupId : null}
        onCancel={() => {
          setShareModalVisible(false);
          setShareTarget(null);
        }}
        onSuccess={() => {
          setShareModalVisible(false);
          setShareTarget(null);
        }}
      />
    </LayoutWrapper>
  );
};

export default FileManager;
